import React, { ReactNode, useContext } from 'react'
import { GetCompanyDetailsQuery, useGetCompanyDetailsQuery } from '../../__generated__/graphql'
import { useParams } from 'react-router-dom'
import { CompanyRoute } from '../routeParams'
import { useDocumentTitle } from '../../utils/providers/documentTitleContext'
import PwsSpinner from '../../components/loader/PwsSpinner'

const CurrentCompanyDisplayedContext = React.createContext<GetCompanyDetailsQuery | undefined>(undefined)
CurrentCompanyDisplayedContext.displayName = 'CurrentCompanyDisplayedContext'

type CurrentCompanyDisplayedProviderProps = {
  slug?: string
  children: ReactNode
}

const CurrentCompanyDisplayedProviderWithSlug: React.FC<{ slug: string; children: ReactNode }> = ({
  slug,
  children,
}) => {
  const { data, loading } = useGetCompanyDetailsQuery({
    variables: { slug },
  })

  useDocumentTitle(`${data?.company.name ?? ''} | Powerspace Ads`)
  return (
    <CurrentCompanyDisplayedContext.Provider value={data}>
      {loading ? <PwsSpinner /> : children}
    </CurrentCompanyDisplayedContext.Provider>
  )
}

const CurrentCompanyDisplayedProviderWithParams: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { slug } = useParams<CompanyRoute>()

  return <CurrentCompanyDisplayedProviderWithSlug slug={slug}>{children}</CurrentCompanyDisplayedProviderWithSlug>
}

export const CurrentCompanyDisplayedProvider: React.FC<CurrentCompanyDisplayedProviderProps> = ({ slug, children }) => {
  if (slug)
    return <CurrentCompanyDisplayedProviderWithSlug slug={slug}>{children}</CurrentCompanyDisplayedProviderWithSlug>

  return <CurrentCompanyDisplayedProviderWithParams>{children}</CurrentCompanyDisplayedProviderWithParams>
}

export function useMaybeCurrentCompanyDisplayedContext(): GetCompanyDetailsQuery | undefined {
  return useContext(CurrentCompanyDisplayedContext)
}

export function useCurrentCompanyDisplayedContext(): GetCompanyDetailsQuery {
  const value = useMaybeCurrentCompanyDisplayedContext()
  if (!value) throw new Error('provider was not used')
  return value
}
